"use client";

import React, { ReactNode, useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import type {
    FormSchema,
    TurnstileFormDisplayImage,
    TurnstileFormHiddenPayload,
    TurnstileFormReadOnlyField,
} from "../types";
import { validateForm } from "../validate";
import {
    baseLayoutTokens,
    formThemeTokens,
    layoutTokens,
    mergeStyles,
    typographyTokens,
    uiTokens,
    widthPixels,
    type TurnstileFormLayout,
    type TurnstileFormTheme,
    type TurnstileFormTriggerVariant,
    type TurnstileFormWidth,
} from "./TurnstileForm.tokens";

export type TurnstileFormStatus = "idle" | "success" | "error";
export type TurnstileFormRenderMode = "form" | "modal-button";
export type TurnstileFormTriggerIconPosition = "before" | "after";

const EMPTY_INITIAL_VALUES: Record<string, string> = {};
const EMPTY_READ_ONLY_FIELDS: TurnstileFormReadOnlyField[] = [];
const EMPTY_HIDDEN_PAYLOAD: TurnstileFormHiddenPayload = {};



type TurnstileFormOption = {
    label: string;
    value: string;
};

type TurnstileFormRenderableField = {
    name: string;
    label?: string;
    type: "hidden" | "select" | "dropdown" | "textarea" | "checkbox" | "file" | "text" | "email" | "tel" | "url" | string;
    required?: boolean;
    placeholder?: string;
    value?: string;
    minLength?: number;
    maxLength?: number;
    rows?: number;
    options?: TurnstileFormOption[];
    accept?: string;
    multiple?: boolean;
};

export type TurnstileFormProps = {
    schema: FormSchema;
    workerUrl: string;
    siteKey: string;
    formName?: string;
    maxWidth?: TurnstileFormWidth;
    theme?: TurnstileFormTheme;
    layout?: TurnstileFormLayout;
    submitText?: string;
    loadingText?: string;
    successMessage?: ReactNode;
    rejectedMessage?: string;
    errorMessage?: string;
    autoHideMs?: number;
    initialValues?: Record<string, string>;
    displayImage?: TurnstileFormDisplayImage;
    readOnlyFields?: TurnstileFormReadOnlyField[];
    hiddenPayload?: TurnstileFormHiddenPayload;
    onSuccess?: (response: unknown) => void;
    onError?: (error: unknown) => void;

    /** Keeps the existing inline form by default. Use "modal-button" to render a trigger button that opens this same form inside a modal. */
    renderMode?: TurnstileFormRenderMode;
    triggerText?: string;
    triggerVariant?: TurnstileFormTriggerVariant;
    triggerIcon?: LucideIcon;
    triggerIconPosition?: TurnstileFormTriggerIconPosition;
    modalTitle?: ReactNode;
    modalCloseText?: string;
};

declare global {
    interface Window {
        turnstile?: {
            render: (el: HTMLElement, options: any) => string;
            execute: (widgetId: string) => void;
            reset: (widgetId: string) => void;
        };
    }
}

function ensureTurnstileScript() {
    if (typeof document === "undefined") return;
    if (document.querySelector("#cloudflare-turnstile-script")) return;

    const script = document.createElement("script");
    script.id = "cloudflare-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
}

export function TurnstileForm({
                                  schema,
                                  workerUrl,
                                  siteKey,
                                  formName = "contact",
                                  maxWidth = "lg",
                                  theme = "light",
                                  layout = "two-column",
                                  submitText = "Submit",
                                  loadingText = "Sending…",
                                  successMessage = "✅ Thanks! Your message has been sent!",
                                  rejectedMessage = "We couldn’t verify your request. Please try again.",
                                  errorMessage = "Something went wrong. Please try again.",
                                  autoHideMs = 10000,
                                  initialValues = EMPTY_INITIAL_VALUES,
                                  displayImage,
                                  readOnlyFields = EMPTY_READ_ONLY_FIELDS,
                                  hiddenPayload = EMPTY_HIDDEN_PAYLOAD,
                                  onSuccess,
                                  onError,
                                  renderMode = "form",
                                  triggerText = "Open form",
                                  triggerVariant = "blue",
                                  triggerIcon: TriggerIcon,
                                  triggerIconPosition = "before",
                                  modalTitle,
                                  modalCloseText = "Close",
                              }: TurnstileFormProps) {
    const [values, setValues] = useState<Record<string, string>>(initialValues);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState<TurnstileFormStatus>("idle");
    const [error, setError] = useState<string | null>(null);
    const [resetKey, setResetKey] = useState(0);
    const [showBanner, setShowBanner] = useState(true);
    const [focusedField, setFocusedField] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const turnstilePromiseRef = useRef<Promise<string> | null>(null);

    const t = formThemeTokens[theme];
    const isModalButtonMode = renderMode === "modal-button";

    useEffect(() => {
        ensureTurnstileScript();
    }, []);

    useEffect(() => {
        setValues(initialValues);
    }, [initialValues]);

    function handleChange(name: string, value: string) {
        setValues((current) => ({
            ...current,
            [name]: value,
        }));
    }

    async function handleFileChange(
        name: string,
        files: FileList | null,
        multiple?: boolean,
    ) {
        const selectedFiles = Array.from(files ?? []);

        if (selectedFiles.length === 0) {
            handleChange(name, "");
            return;
        }

        const filesToRead = multiple ? selectedFiles : selectedFiles.slice(0, 1);

        const encodedFiles = await Promise.all(
            filesToRead.map(
                (file) =>
                    new Promise<{
                        name: string;
                        type: string;
                        size: number;
                        dataUrl: string;
                    }>((resolve, reject) => {
                        const reader = new FileReader();

                        reader.onload = () => {
                            resolve({
                                name: file.name,
                                type: file.type,
                                size: file.size,
                                dataUrl: String(reader.result ?? ""),
                            });
                        };

                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(file);
                    }),
            ),
        );

        handleChange(
            name,
            multiple ? JSON.stringify(encodedFiles) : JSON.stringify(encodedFiles[0]),
        );
    }

    function closeModal() {
        setIsModalOpen(false);
        containerRef.current?.replaceChildren();
        widgetIdRef.current = null;
        turnstilePromiseRef.current = null;
    }

    function ensureTurnstileWidget(): Promise<string> {
        if (turnstilePromiseRef.current) return turnstilePromiseRef.current;

        turnstilePromiseRef.current = new Promise((resolve, reject) => {
            if (typeof window === "undefined" || !containerRef.current || !siteKey) {
                turnstilePromiseRef.current = null;
                reject(new Error("REQUEST_REJECTED"));
                return;
            }

            let finished = false;

            const finish = (fn: () => void) => {
                if (finished) return;
                finished = true;
                turnstilePromiseRef.current = null;
                fn();
            };

            const timeoutId = window.setTimeout(() => {
                finish(() => reject(new Error("REQUEST_REJECTED")));
            }, 10000);

            const waitForTurnstile = () => {
                if (!window.turnstile) {
                    window.setTimeout(waitForTurnstile, 100);
                    return;
                }

                try {
                    containerRef.current!.innerHTML = "";

                    widgetIdRef.current = window.turnstile.render(containerRef.current!, {
                        sitekey: siteKey,
                        execution: "execute",
                        size: "normal",
                        callback: (token: string) => {
                            clearTimeout(timeoutId);
                            finish(() => resolve(token));
                        },
                        "error-callback": () => {
                            clearTimeout(timeoutId);
                            finish(() => reject(new Error("REQUEST_REJECTED")));
                        },
                    });

                    window.turnstile.execute(widgetIdRef.current);
                } catch {
                    clearTimeout(timeoutId);
                    finish(() => reject(new Error("REQUEST_REJECTED")));
                }
            };

            waitForTurnstile();
        });

        return turnstilePromiseRef.current;
    }

    async function submit() {
        if (isSubmitting) return;

        const validationErrors = validateForm(schema, values);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) return;

        setIsSubmitting(true);
        setStatus("idle");
        setError(null);

        try {
            const turnstileToken = await ensureTurnstileWidget();

            const readOnlyPayload = readOnlyFields.reduce<Record<string, string>>(
                (acc, field) => {
                    acc[field.name] = field.value;
                    return acc;
                },
                {},
            );

            const payloadData = {
                ...values,
                ...readOnlyPayload,
                ...hiddenPayload,
            };

            const response = await fetch(workerUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    form: formName,
                    data: payloadData,
                    turnstileToken,
                }),
            });

            const json = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(json?.error ?? "REQUEST_REJECTED");
            }

            setStatus("success");
            setErrors({});
            setValues(initialValues);
            setResetKey((key) => key + 1);
            containerRef.current?.replaceChildren();
            widgetIdRef.current = null;
            turnstilePromiseRef.current = null;
            if (isModalButtonMode) setIsModalOpen(false);
            onSuccess?.(json);
        } catch (err: any) {
            const message =
                err?.message === "REQUEST_REJECTED" ? rejectedMessage : errorMessage;
            setStatus("error");
            setError(message);
            onError?.(err);
        } finally {
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        setShowBanner(true);
    }, [status]);

    useEffect(() => {
        if (status !== "success") return;
        if (isModalButtonMode) return;

        const timer = window.setTimeout(() => {
            setShowBanner(false);
        }, autoHideMs);

        return () => clearTimeout(timer);
    }, [status, autoHideMs, isModalButtonMode]);

    const bannerStyles = status === "success" ? t.successBanner : t.errorBanner;
    const dismissStyles =
        status === "success" ? t.successDismiss : t.errorDismiss;

    function renderBanner(allowedStatuses: TurnstileFormStatus[]) {
        const isVisible =
            showBanner &&
            (status === "success" || status === "error") &&
            allowedStatuses.includes(status);

        if (!isVisible) return null;

        return (
            <div style={mergeStyles(baseLayoutTokens.banner, bannerStyles)}>
                <div
                    style={mergeStyles(baseLayoutTokens.bannerInner, {
                        maxWidth: widthPixels[maxWidth],
                    })}
                >
                    <button
                        type="button"
                        onClick={() => setShowBanner(false)}
                        style={mergeStyles(baseLayoutTokens.dismissButton, dismissStyles)}
                        aria-label="Dismiss message"
                    >
                        ×
                    </button>

                    <div>{status === "success" ? successMessage : `❌ ${error}`}</div>
                </div>
            </div>
        );
    }

    const formPanel = (
        <div
            key={resetKey}
            role="form"
            style={mergeStyles(
                baseLayoutTokens.container,
                {
                    maxWidth: widthPixels[maxWidth],
                },
                t.container,
                isModalButtonMode && {
                    boxShadow: "none",
                    padding: 0,
                },
            )}
        >
            {(displayImage || readOnlyFields.length > 0) && (
                <div style={baseLayoutTokens.contextPanel}>
                    {displayImage && (
                        <div style={baseLayoutTokens.contextImageRow}>
                            <div
                                style={mergeStyles(
                                    baseLayoutTokens.contextImageWrap,
                                    t.contextImageWrap,
                                )}
                            >
                                <img
                                    src={displayImage.src}
                                    alt={displayImage.alt}
                                    width={displayImage.width}
                                    height={displayImage.height}
                                    style={baseLayoutTokens.contextImage}
                                />
                            </div>
                            <div
                                aria-hidden="true"
                                style={baseLayoutTokens.contextImageSpacer}
                            />
                        </div>
                    )}

                    {readOnlyFields.length > 0 && (
                        <div style={baseLayoutTokens.readOnlyGrid}>
                            {readOnlyFields.map((field) => (
                                <div
                                    key={field.name}
                                    style={mergeStyles(
                                        baseLayoutTokens.readOnlyItem,
                                        t.readOnlyItem,
                                    )}
                                >
                                    <div
                                        style={mergeStyles(
                                            typographyTokens.readOnlyLabel,
                                            t.readOnlyLabel,
                                        )}
                                    >
                                        {field.label}
                                    </div>
                                    <div
                                        style={mergeStyles(
                                            typographyTokens.readOnlyValue,
                                            t.readOnlyValue,
                                        )}
                                    >
                                        {field.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div style={layoutTokens[layout]}>
                {(schema.fields as TurnstileFormRenderableField[]).map((field) => {
                    if (field.type === "hidden") {
                        return (
                            <input
                                key={field.name}
                                type="hidden"
                                name={field.name}
                                value={values[field.name] ?? field.value ?? ""}
                                readOnly
                            />
                        );
                    }

                    const value = values[field.name] ?? "";
                    const fieldError = errors[field.name];
                    const isFocused = focusedField === field.name;
                    const inputStyle = mergeStyles(
                        uiTokens.input,
                        typographyTokens.input,
                        t.input,
                        isFocused && t.inputFocus,
                    );
                    const textareaStyle = mergeStyles(
                        uiTokens.textarea,
                        typographyTokens.textarea,
                        t.textarea,
                        isFocused && t.textareaFocus,
                    );

                    return (
                        <div key={field.name} style={baseLayoutTokens.field}>
                            {field.type !== "checkbox" && (
                                <label
                                    htmlFor={field.name}
                                    style={mergeStyles(typographyTokens.label, t.label)}
                                >
                                    {field.label}
                                    {field.required && <span style={uiTokens.required}>*</span>}
                                </label>
                            )}

                            {field.type === "textarea" && (
                                <textarea
                                    id={field.name}
                                    name={field.name}
                                    value={value}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                    minLength={field.minLength}
                                    maxLength={field.maxLength}
                                    rows={field.rows}
                                    onFocus={() => setFocusedField(field.name)}
                                    onBlur={() => setFocusedField(null)}
                                    onChange={(event) =>
                                        handleChange(field.name, event.target.value)
                                    }
                                    style={textareaStyle}
                                />
                            )}

                            {(field.type === "select" || field.type === "dropdown") && (
                                <select
                                    id={field.name}
                                    name={field.name}
                                    value={value}
                                    required={field.required}
                                    onFocus={() => setFocusedField(field.name)}
                                    onBlur={() => setFocusedField(null)}
                                    onChange={(event) =>
                                        handleChange(field.name, event.target.value)
                                    }
                                    style={inputStyle}
                                >
                                    <option value="">
                                        {field.placeholder ?? "Select an option"}
                                    </option>

                                    {field.options?.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {field.type === "checkbox" && (
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        cursor: "pointer",
                                    }}
                                >
                                    <input
                                        id={field.name}
                                        name={field.name}
                                        type="checkbox"
                                        checked={value === "true"}
                                        required={field.required}
                                        onFocus={() => setFocusedField(field.name)}
                                        onBlur={() => setFocusedField(null)}
                                        onChange={(event) =>
                                            handleChange(field.name, event.target.checked ? "true" : "")
                                        }
                                        style={{ width: 18, height: 18 }}
                                    />
                                    <span style={mergeStyles(typographyTokens.input, t.label)}>
                    {field.label}
                                        {field.required && <span style={uiTokens.required}>*</span>}
                  </span>
                                </label>
                            )}

                            {field.type === "file" && (
                                <input
                                    id={field.name}
                                    name={field.name}
                                    type="file"
                                    required={field.required}
                                    accept={field.accept}
                                    multiple={field.multiple}
                                    onFocus={() => setFocusedField(field.name)}
                                    onBlur={() => setFocusedField(null)}
                                    onChange={(event) =>
                                        void handleFileChange(
                                            field.name,
                                            event.target.files,
                                            field.multiple,
                                        )
                                    }
                                    style={inputStyle}
                                />
                            )}

                            {field.type !== "textarea" &&
                                field.type !== "select" &&
                                field.type !== "dropdown" &&
                                field.type !== "checkbox" &&
                                field.type !== "file" && (
                                    <input
                                        id={field.name}
                                        name={field.name}
                                        type={field.type}
                                        value={value}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                        minLength={field.minLength}
                                        maxLength={field.maxLength}
                                        onFocus={() => setFocusedField(field.name)}
                                        onBlur={() => setFocusedField(null)}
                                        onChange={(event) =>
                                            handleChange(field.name, event.target.value)
                                        }
                                        style={inputStyle}
                                    />
                                )}

                            {fieldError && (
                                <p style={mergeStyles(typographyTokens.error, t.error)}>
                                    {fieldError}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            <div style={baseLayoutTokens.footer}>
                <div ref={containerRef} style={baseLayoutTokens.hiddenTurnstile} />

                <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={submit}
                    style={mergeStyles(
                        uiTokens.button,
                        typographyTokens.button,
                        t.button,
                        isSubmitting && t.buttonDisabled,
                    )}
                >
                    {isSubmitting ? loadingText : submitText}
                </button>
            </div>
        </div>
    );

    const inlineBanner = renderBanner(["success", "error"]);
    const modalSuccessBanner = renderBanner(["success"]);
    const modalErrorBanner = renderBanner(["error"]);

    if (!isModalButtonMode) {
        return (
            <section style={baseLayoutTokens.section}>
                {formPanel}
                {inlineBanner}
            </section>
        );
    }

    const triggerIcon = TriggerIcon ? (
        <span aria-hidden="true" style={baseLayoutTokens.triggerIcon}>
      <TriggerIcon size={16} strokeWidth={2.2} />
    </span>
    ) : null;

    return (
        <section style={baseLayoutTokens.section}>
            <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                style={mergeStyles(
                    uiTokens.button,
                    typographyTokens.button,
                    {
                        gap: 8,
                    },
                    t.triggerButton[triggerVariant],
                )}
            >
                {triggerIconPosition === "before" && triggerIcon}
                <span>{triggerText}</span>
                {triggerIconPosition === "after" && triggerIcon}
            </button>

            {modalSuccessBanner}

            {isModalOpen && (
                <div
                    role="presentation"
                    style={baseLayoutTokens.modalOverlay}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={
                            typeof modalTitle === "string" ? modalTitle : triggerText
                        }
                        style={mergeStyles(baseLayoutTokens.modalPanel, t.modalPanel, {
                            maxWidth: widthPixels[maxWidth],
                            padding: 20,
                        })}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div style={baseLayoutTokens.modalHeader}>
                            {modalTitle ? (
                                <h2 style={baseLayoutTokens.modalTitle}>{modalTitle}</h2>
                            ) : (
                                <span />
                            )}

                            <button
                                type="button"
                                aria-label="Close form"
                                onClick={closeModal}
                                style={mergeStyles(
                                    baseLayoutTokens.modalCloseIconButton,
                                    t.modalCloseIconButton,
                                )}
                            >
                                ×
                            </button>
                        </div>

                        {modalErrorBanner}

                        {formPanel}

                        <div style={baseLayoutTokens.modalActions}>
                            <button
                                type="button"
                                onClick={closeModal}
                                style={mergeStyles(
                                    uiTokens.button,
                                    typographyTokens.modalCloseButton,
                                    t.modalCloseButton,
                                )}
                            >
                                {modalCloseText}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

export default TurnstileForm;
